import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { X } from "lucide-react";

// Bento spans — applied in rotation across project tiles.
const SPANS = [
  "md:col-span-2 md:row-span-3",
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-2",
  "md:col-span-1 md:row-span-3",
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-2",
  "md:col-span-2 md:row-span-3",
  "md:col-span-1 md:row-span-2",
];

function useImagePreload(urls) {
  useEffect(() => {
    const imgs = urls.map((u) => {
      const img = new Image();
      img.decoding = "async";
      img.src = u;
      return img;
    });
    return () => imgs.forEach((i) => (i.src = ""));
  }, [urls]);
}

function ProjectModal({ project, photoIdx, setPhotoIdx, onClose }) {
  const [dockPos, setDockPos] = useState({ x: 0, y: 0 });
  const photos = project.images;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setPhotoIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft")
        setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, setPhotoIdx, onClose]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[70] bg-charcoal/95 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-5xl aspect-[16/10] overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={photoIdx}
              src={photos[photoIdx]}
              alt={`${project.title} — ${photoIdx + 1}`}
              loading="eager"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          </AnimatePresence>

          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-charcoal via-charcoal/65 to-transparent">
            <p className="text-[11px] tracking-[0.3em] uppercase text-brass">
              {project.tag} · {project.type}
            </p>
            <h3 className="mt-2 font-display text-2xl md:text-4xl text-cream leading-tight">
              {project.title}
            </h3>
            <p className="mt-2 text-cream/75 text-sm md:text-base max-w-2xl">
              {project.sub}
            </p>
            <p className="mt-2 text-cream/55 text-[11px] tracking-[0.22em] uppercase">
              {project.budget} · {project.time} · Photo {photoIdx + 1} of {photos.length}
            </p>
          </div>

          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            className="absolute top-3 right-3 p-2.5 rounded-full bg-charcoal/70 text-cream hover:bg-brass hover:text-charcoal backdrop-blur-sm transition-colors"
            aria-label="Close"
            data-testid="bento-modal-close"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>

      {/* Draggable dock — switch between photos OF THIS PROJECT */}
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0.1}
        initial={false}
        animate={{ x: dockPos.x, y: dockPos.y }}
        onDragEnd={(_, info) =>
          setDockPos((prev) => ({
            x: prev.x + info.offset.x,
            y: prev.y + info.offset.y,
          }))
        }
        className="fixed z-[72] left-1/2 bottom-6 -translate-x-1/2 touch-none"
      >
        <div className="relative rounded-full bg-charcoal/80 backdrop-blur-xl border border-brass/30 shadow-2xl cursor-grab active:cursor-grabbing">
          <div className="flex items-center -space-x-2 px-4 py-3">
            {photos.map((url, i) => (
              <motion.button
                key={url + i}
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoIdx(i);
                }}
                style={{ zIndex: photoIdx === i ? 30 : photos.length - i }}
                className={`relative w-9 h-9 sm:w-11 sm:h-11 flex-shrink-0 rounded-full overflow-hidden cursor-pointer ${
                  photoIdx === i
                    ? "ring-2 ring-brass shadow-lg"
                    : "hover:ring-2 hover:ring-cream/40"
                }`}
                initial={false}
                animate={{
                  scale: photoIdx === i ? 1.3 : 1,
                  rotate: photoIdx === i ? 0 : i % 2 === 0 ? -10 : 10,
                  y: photoIdx === i ? -10 : 0,
                }}
                whileHover={{
                  scale: 1.4,
                  rotate: 0,
                  y: -12,
                  transition: { type: "spring", stiffness: 400, damping: 25 },
                }}
                aria-label={`Photo ${i + 1}`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
                {photoIdx === i && (
                  <motion.div
                    layoutId="activeGlow"
                    className="absolute -inset-2 bg-brass/30 blur-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default function InteractiveBentoGallery({ projects }) {
  const [selected, setSelected] = useState(null); // { projectIndex }
  const [photoIdx, setPhotoIdx] = useState(0);
  const savedScrollY = useRef(0);

  // Preload ALL photos so switching is instant
  const allUrls = useMemo(
    () => projects.flatMap((p) => p.images),
    [projects]
  );
  useImagePreload(allUrls);

  // Apply rotating span to each project tile
  const items = useMemo(
    () =>
      projects.map((p, i) => ({
        ...p,
        span: SPANS[i % SPANS.length],
        cover: p.images[0],
      })),
    [projects]
  );

  // Lock body scroll + restore exact position on close
  useEffect(() => {
    if (selected != null) {
      savedScrollY.current = window.scrollY;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.position = "fixed";
      document.body.style.top = `-${savedScrollY.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        document.body.style.paddingRight = "";
        window.scrollTo(0, savedScrollY.current);
      };
    }
  }, [selected]);

  const openProject = (i) => {
    setSelected(i);
    setPhotoIdx(0);
  };
  const project = selected != null ? items[selected] : null;

  return (
    <div className="w-full">
      <LayoutGroup>
        <motion.div
          layout
          className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 auto-rows-[80px] md:auto-rows-[110px]"
        >
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                layout
                data-testid="bento-tile"
                className={`relative overflow-hidden cursor-pointer group ${item.span}`}
                onClick={() => openProject(i)}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{
                  layout: { type: "spring", stiffness: 260, damping: 28 },
                  duration: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <img
                  src={item.cover}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
                />
                <div className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.28em] uppercase text-cream/85 bg-charcoal/55 backdrop-blur-sm px-2.5 py-1">
                  {item.images.length} photos
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-5 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-brass">
                    {item.tag}
                  </p>
                  <h3 className="mt-1.5 font-display text-cream text-base md:text-lg leading-snug line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-cream/70 text-[11px] md:text-xs mt-0.5 line-clamp-1">
                    {item.sub}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </LayoutGroup>

      <AnimatePresence>
        {project && (
          <ProjectModal
            project={project}
            photoIdx={photoIdx}
            setPhotoIdx={setPhotoIdx}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export { SPANS };
